"use client";
import { createContext, useContext, useState, useCallback, ReactNode, FC } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface SnackAlertState {
  message: string;
  open: boolean;
  severity: AlertColor;
}

interface SnackAlertContextType {
  hideSnackbar: () => void;
  showSnackbar: (message: string, severity?: AlertColor) => void;
}

const SnackAlertContext = createContext<SnackAlertContextType | undefined>(undefined);

export const SnackAlertProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [snackAlert, setSnackAlert] = useState<SnackAlertState>({
    open: false,
    message: '',
    severity: 'info',
  });

  const showSnackbar = useCallback((message: string, severity: AlertColor = 'info') => {
    setSnackAlert({
      open: true,
      message,
      severity,
    });
  }, []);

  const hideSnackbar = useCallback(() => {
    setSnackAlert(prev => ({ ...prev, open: false }));
  }, []);

  return (
    <SnackAlertContext.Provider value={{ showSnackbar, hideSnackbar }}>
      {children}
      <Snackbar
        open={snackAlert.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideSnackbar} severity={snackAlert.severity} className="w-full">
          {snackAlert.message}
        </Alert>
      </Snackbar>
    </SnackAlertContext.Provider>
  );
};

export const useSnackAlert = () => {
  const context = useContext(SnackAlertContext);
  if (!context) {
    throw new Error('useSnackAlert must be used within a SnackAlertProvider');
  }
  return context;
};
